import {
  type CSSProperties,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useAttrs,
  useChainedCommands,
  useCurrentSelection,
  useEditorFocus,
  usePositioner,
  useUpdateReason,
} from "@remirror/react";
import { getMarkRange } from "remirror";
import { createMarkPositioner, getPositioner } from "remirror/extensions";

import { Button, Icon, Popup } from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

import { FocusContext } from "./FocusContext";

const getLinkHrefFromSelection = (
  attrs: ReturnType<typeof useAttrs>,
  selection: ReturnType<typeof useCurrentSelection>,
) => {
  const hrefFromAttrs = attrs.link()?.href;
  if (hrefFromAttrs) {
    return String(hrefFromAttrs);
  }

  for (const $pos of [selection.$from, selection.$to]) {
    const linkMark = $pos
      .marks()
      .find(
        (mark: { type: { name: string }; attrs: { href?: string } }) =>
          mark.type.name === "link",
      );
    if (linkMark?.attrs?.href) {
      return String(linkMark.attrs.href);
    }
  }

  return "";
};

function useFloatingLinkState() {
  const chain = useChainedCommands();
  const selection = useCurrentSelection();
  const [editorFocus] = useEditorFocus();
  const { empty } = selection;

  const [editingState, setEditingState] = useState({
    isEditing: false,
    href: "",
  });

  const attrs = useAttrs();
  const url = useMemo(
    () => getLinkHrefFromSelection(attrs, selection),
    [attrs, selection],
  );

  useEffect(() => {
    if (!editingState.isEditing && editingState.href !== url) {
      setEditingState((prev) => ({ ...prev, href: url }));
    }
  }, [url, editingState.isEditing, editingState.href]);

  useEffect(() => {
    if (empty || !editorFocus) {
      setEditingState((prev) => ({ ...prev, href: "", isEditing: false }));
    }
  }, [empty, editorFocus]);

  // A positioner which only shows for links.
  const linkPositioner = useMemo(
    () => createMarkPositioner({ type: "link" }),
    [],
  );
  const onRemove = useCallback(() => chain.removeLink().focus().run(), [chain]);
  const submitHref = useCallback(() => {
    setEditingState((prev) => ({ ...prev, isEditing: false }));

    if (editingState.href === "") {
      chain.removeLink();
    } else {
      const markRange = getMarkRange(selection.$from, "link");
      chain.updateLink({ href: editingState.href, auto: false }, markRange);
    }

    chain.focus(selection?.to).run();
  }, [chain, editingState.href, selection]);

  const cancelHref = useCallback(() => {
    setEditingState((prev) => ({ ...prev, isEditing: false }));
    chain.focus().run();
  }, [chain]);

  useEffect(() => {
    if (!editingState) {
      return;
    }
    const keyDownHandler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        cancelHref();
      }
      if (event.key === "Enter") {
        submitHref();
      }
    };

    document.addEventListener("keydown", keyDownHandler);

    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  }, [editingState, cancelHref, submitHref]);

  const clickEdit = useCallback(() => {
    if (empty) {
      chain.selectLink();
    }

    setEditingState((prev) => ({ ...prev, isEditing: true, href: url }));
  }, [chain, url, empty]);

  return {
    url,
    editingState,
    setEditingState,
    linkPositioner,
    clickEdit,
    onRemove,
    submitHref,
    cancelHref,
    selection: { empty },
  };
}

const FloatingToolbar = ({
  positioner = "selection",
  children,
  isEditing,
}: {
  positioner?: string;
  children: React.ReactNode;
  isEditing: boolean;
}) => {
  const { ref, x, y, width, height, active } = usePositioner(
    () => getPositioner(positioner),
    [positioner],
  );
  const { isFocused } = useContext(FocusContext);

  const inlineStyle: CSSProperties = useMemo(
    () => ({
      position: "absolute",
      pointerEvents: "none",
      left: x,
      top: y,
      width,
      height,
    }),
    [x, y, width, height],
  );

  if (!isFocused && !isEditing) {
    return null;
  }

  return (
    <Popup
      trigger={<div ref={ref} style={inlineStyle} />}
      open={active}
      content={<div>{children}</div>}
      basic
      style={{ padding: 0, border: "none" }}
    />
  );
};

export const FloatingLinkToolbar = () => {
  const {
    url,
    editingState,
    setEditingState,
    linkPositioner,
    clickEdit,
    onRemove,
    submitHref,
    cancelHref,
  } = useFloatingLinkState();

  const handleClickEdit = useCallback(() => {
    clickEdit();
  }, [clickEdit]);

  const urlInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editingState.isEditing) {
      return;
    }
    requestAnimationFrame(() => {
      urlInputRef.current?.focus({ preventScroll: true });
    });
  }, [editingState.isEditing]);

  return (
    <FloatingToolbar
      positioner={url ? linkPositioner : undefined}
      isEditing={editingState.isEditing}
    >
      {editingState.isEditing ?
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Input
            ref={urlInputRef}
            type="text"
            value={editingState.href}
            placeholder="Enter link..."
            onChange={(e) => {
              setEditingState((prev) => ({ ...prev, href: e.target.value }));
            }}
            style={{ width: 450 }}
            action
          />
          <Button basic icon onClick={submitHref} type="submit">
            <Icon name="check" />
          </Button>
          <Button basic icon onClick={cancelHref}>
            <Icon name="close" />
          </Button>
        </div>
      : url ?
        <div style={{ position: "relative" }}>
          <div
            style={{
              position: "absolute",
              padding: "4px 8px",
              bottom: 36,
              left: 0,
              background: "var(--colorNeutralBackground4, #eaeaea)",
              borderRadius: 4,
              alignItems: "center",
              fontSize: "0.75rem",
              lineHeight: "0.85rem",
              wordBreak: "break-all",
              maxWidth: 350,
              width: "max-content",
              gap: 3,
            }}
          >
            <Icon
              name="linkify"
              size="small"
              style={{ top: -1, position: "relative" }}
            />
            {url}
          </div>
          <Button.Group>
            <Button basic onClick={handleClickEdit} icon>
              <Icon name="pencil" />
            </Button>
            <Button basic onClick={onRemove} icon>
              <Icon name="unlinkify" />
            </Button>
          </Button.Group>
        </div>
      : <Button.Group>
          <Button basic onClick={handleClickEdit} icon>
            <Icon name="linkify" />
          </Button>
        </Button.Group>
      }
    </FloatingToolbar>
  );
};
